function isHeicLike(file: File): boolean {
  const type = file.type.toLowerCase();
  const name = file.name.toLowerCase();
  return type.includes("heic") || type.includes("heif") || name.endsWith(".heic") || name.endsWith(".heif");
}

function shouldTranscodeForUpload(file: File): boolean {
  // Backend base64-encodes the image before sending to RunPod (10 MiB limit).
  // 7 MiB × 4/3 ≈ 9.3 MiB — safe headroom below the RunPod limit.
  const maxSafeBytes = 7 * 1024 * 1024;
  return isHeicLike(file) || file.size > maxSafeBytes;
}

function buildJpegFileName(file: File): string {
  const original = file.name && file.name.trim().length > 0 ? file.name : "upload.jpg";
  const dotIndex = original.lastIndexOf(".");
  const base = dotIndex > 0 ? original.slice(0, dotIndex) : original;
  return `${base}.jpg`;
}

async function loadImageElement(file: File): Promise<HTMLImageElement> {
  const objectUrl = URL.createObjectURL(file);
  return await new Promise<HTMLImageElement>((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error("Failed to decode image"));
    };
    img.src = objectUrl;
  });
}

// The backend base64-encodes the image before submitting to RunPod (10 MiB JSON body limit).
// 7 MiB × 4/3 ≈ 9.3 MiB — keeps the base64 payload safely under the RunPod limit.
const MAX_UPLOAD_BYTES = 7 * 1024 * 1024;

function encodeCanvasToJpeg(canvas: HTMLCanvasElement, quality: number): Promise<Blob> {
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (value) => {
        if (!value) reject(new Error("Failed to encode JPEG"));
        else resolve(value);
      },
      "image/jpeg",
      quality,
    );
  });
}

async function transcodeToJpeg(file: File): Promise<File> {
  const img = await loadImageElement(file);
  const maxEdge = 2048;
  const maxCurrentEdge = Math.max(img.width, img.height);
  const scale = maxCurrentEdge > maxEdge ? maxEdge / maxCurrentEdge : 1;
  const width = Math.max(1, Math.round(img.width * scale));
  const height = Math.max(1, Math.round(img.height * scale));

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext("2d");
  if (!context) {
    throw new Error("Canvas 2D context is unavailable");
  }

  context.drawImage(img, 0, 0, width, height);

  let blob = await encodeCanvasToJpeg(canvas, 0.92);

  // Progressively reduce quality until the file fits within the upload limit.
  for (const quality of [0.80, 0.70, 0.55]) {
    if (blob.size <= MAX_UPLOAD_BYTES) break;
    blob = await encodeCanvasToJpeg(canvas, quality);
  }

  return new File([blob], buildJpegFileName(file), {
    type: "image/jpeg",
    lastModified: Date.now(),
  });
}

export async function prepareImageForUpload(image: File): Promise<File> {
  if (!shouldTranscodeForUpload(image)) {
    return image;
  }

  return await transcodeToJpeg(image);
}
